import { isDevToolboxEnabled } from "./isDevToolboxEnabled";
import type { DevToolboxScenarioId } from "./types";

type ScenarioRuntimeModule = typeof import("./scenarioRuntime");

let runtimePromise: Promise<ScenarioRuntimeModule> | null = null;

/** Shared dev-only import; production builds fold callers to no-ops. */
export function loadScenarioRuntime(): Promise<ScenarioRuntimeModule | null> {
  if (!isDevToolboxEnabled()) return Promise.resolve(null);
  runtimePromise ??= import("./scenarioRuntime");
  return runtimePromise;
}

/** Warm scenario runtime + shell chunks after first paint (dev only). */
export function preloadDevToolboxModules(): void {
  if (!isDevToolboxEnabled()) return;
  void loadScenarioRuntime();
}

/**
 * Subscribe to active scenario changes without statically importing scenarioRuntime.
 * Production builds eliminate this branch when isDevToolboxEnabled() is false.
 */
export function bindDevToolboxScenarioListener(
  listener: () => void,
): () => void {
  if (!isDevToolboxEnabled()) return () => {};

  let unsubscribe = () => {};
  let disposed = false;

  void loadScenarioRuntime().then((runtime) => {
    if (!runtime || disposed) return;
    listener();
    unsubscribe = runtime.subscribeToDevToolboxScenario(listener);
  });

  return () => {
    disposed = true;
    unsubscribe();
  };
}

/**
 * Subscribe and run callback with the active scenario id (single runtime load).
 */
export function bindDevToolboxScenarioIdListener(
  run: (scenarioId: DevToolboxScenarioId | null) => void,
): () => void {
  if (!isDevToolboxEnabled()) return () => {};

  let unsubscribe = () => {};
  let disposed = false;

  void loadScenarioRuntime().then((runtime) => {
    if (!runtime || disposed) return;

    const sync = () => {
      if (disposed) return;
      run(runtime.getActiveDevToolboxScenarioId());
    };

    sync();
    unsubscribe = runtime.subscribeToDevToolboxScenario(sync);
  });

  return () => {
    disposed = true;
    unsubscribe();
  };
}

/**
 * Run a dev-only callback with the active scenario id (async runtime load).
 */
export function withActiveDevToolboxScenarioId(
  run: (scenarioId: DevToolboxScenarioId | null) => void,
): void {
  if (!isDevToolboxEnabled()) return;
  void loadScenarioRuntime().then((runtime) => {
    if (!runtime) return;
    run(runtime.getActiveDevToolboxScenarioId());
  });
}
